<script lang="ts" setup>
import { type Specialisation } from '@/models'

const props = defineProps<{ allSpecialisations: Specialisation[] }>()

const axios = useAxios()
const { t } = useI18n()
const { isMobileView } = useScreen()
const router = useRouter()
const localePath = useLocalePath()

const selectedSpecialisationSlugs = ref([])
const selectedLanguages = ref([])
const searchQuery = ref("")
const currentRequest = ref<AbortController | null>(null)
const isEntitiesLoading = ref(false)
const availableFilters = ref([])
const sliderModel = ref([0, 0])
const sliderRange = ref([0, 0]) // Just to store the slider max and min values, don't modify them
const minHourlyRate = ref(0)
const maxHourlyRate = ref(0)
const searchModalRef = ref()

const allLanguages = computed(() => {
  return availableFilters.value.language ? availableFilters.value.language.map(language => ({ id: language.name, label: language.name })) : []
})

function handleSelectFilter(type: string, newValue: string) {
  let selectedArray;

  if (type === "specialisation") {
    selectedArray = selectedSpecialisationSlugs.value
  } else if (type === "language") {
    selectedArray = selectedLanguages.value
  }

  const index = selectedArray.indexOf(newValue)
  index === -1 ? selectedArray.push(newValue) : selectedArray.splice(index, 1);
}

const { isLoading } = useAsyncState(
  async () => {
    const filtersResponse = await axios.get('/lawyers/available-filters', {
      params: {
        specialty: t('advocates')
      }
    })
    availableFilters.value = filtersResponse.data;
  },
  null,
  {
    onSuccess: () => updateSlider(),
    onError: (error) => console.error(error)
  }
)

function resetFilters(filterType: string) {
  if (filterType === "specialisation") {
    selectedSpecialisationSlugs.value = [];
  } else if (filterType === "language") {
    selectedLanguages.value = [];
  } else if (filterType === "hourly_rate") {
    updateSlider();
  }
}
function updateSlider(value?: number[]) {
  if (value) {
    if (value[0] > value[1]) value[0] = 0;
    minHourlyRate.value = value[0] < sliderRange.value[0] ? sliderRange.value[0] : value[0];
    maxHourlyRate.value = value[1] > sliderRange.value[1] ? sliderRange.value[1] : value[1];
  } else {
    minHourlyRate.value = availableFilters.value.hourlyConsultationPriceFrom || 0
    maxHourlyRate.value = availableFilters.value.hourlyConsultationPriceTill || 0
    sliderRange.value = [minHourlyRate.value, maxHourlyRate.value]
  }
  sliderModel.value = [minHourlyRate.value, maxHourlyRate.value]
}

const { state: legalEntities, execute: fetchLegalEntities } = useAsyncState(
  async () => {
    if (currentRequest.value) currentRequest.value.abort()
    currentRequest.value = new AbortController()
    isEntitiesLoading.value = true
    const { data } = await axios.get('lawyers-and-firms', {
      params: {
        specialty: "advocates",
        query: searchQuery.value
      },
      signal: currentRequest.value.signal
    })
    isEntitiesLoading.value = false
    currentRequest.value = null
    return data
  },
  null,
  {
    immediate: false,
  }
)

const onSearchInput = useDebounceFn(() => fetchLegalEntities())

function navigateToLawyersPage() {
  const route = {
    name: 'lawyers-specialty',
    params: {
      specialty: t('advocates').toLowerCase()
    },
    query: {
      page: 1
    }
  }

  if (selectedSpecialisationSlugs.value?.length > 0) {
    route.query.specialisation = selectedSpecialisationSlugs.value
  }

  if (selectedLanguages.value[0] !== '') {
    route.query.language = selectedLanguages.value
  }

  if (searchQuery.value.trim() !== '') {
    route.query.query = searchQuery.value.trim()
  }

  if (minHourlyRate.value > availableFilters.value.hourlyConsultationPriceFrom) {
    route.query.hourlyConsultationPriceFrom = minHourlyRate.value
  }

  if (maxHourlyRate.value !== availableFilters.value.hourlyConsultationPriceTill) {
    route.query.hourlyConsultationPriceTill = maxHourlyRate.value
  }

  router.push(localePath(route))
}
</script>
<template>
  <div class="w-full bg-gray h-80 lg:h-24 rounded-[20px] lg:block">
    <div class="relative flex items-center h-full w-full">    
      <LoadingOverlay v-model="isLoading" wrapperClass="overflow-hidden !p-0 !z-100 !-m-0 rounded-[20px]" />
      <div class="flex flex-col lg:flex-row justify-between items-center w-full h-full relative py-5 lg:py-0 px-6 xl:px-8">
        <GTDropdown 
          :title="$t('specialisations')" 
          class="w-full lg:w-[18%]" 
          labelWrapperClass="justify-between gap-0"
          :isSelected="Boolean(selectedSpecialisationSlugs.length)"
          >
          <template #default="{ isButtons }">
            <GTDropdownOptions 
              v-model="selectedSpecialisationSlugs"
              :data="props.allSpecialisations.map(x => ({ id: x.slug, label: x.name }))"
              :class="['text-left', 'min-w-[210px] text-darkBlue z-100', { 'pb-2': isButtons }]" 
              :multiple="true"
              @change="({ id }) => handleSelectFilter('specialisation', id)" />
          </template>
          <template #buttons="{ isSelected, closeDropdown }">
            <FilterButtons 
              :isSelected="isSelected"
              class="text-darkBlue"
              @action="closeDropdown"
              @reset="() => {
                closeDropdown()
                resetFilters('specialisation')
              }" 
            />
          </template>
        </GTDropdown>
        <div class="h-1 w-[calc(100%+48px)] lg:w-0 lg:h-12 border-t-2 border-l-0 lg:border-t-0 lg:border-l-2 border-darkBlue opacity-5 lg:ml-3 lg:mr-4 " />
        <GTDropdown 
          :title="$t('hourly_rate')"
          class="w-full lg:w-[18%] text-darkBlue"
          labelWrapperClass="justify-between gap-0"
          :isSelected="Boolean(minHourlyRate !== sliderRange[0] || maxHourlyRate !== sliderRange[1])"
        >
          <template #default="{ isButtons }">
            <div class="p-4 grid gap-6">
              <div class="hidden md:block w-full text-center font-semibold">
                <span>{{ $t('hourly_rate') }}</span>
              </div>
              <GTRangeSlider 
                v-model="sliderModel"
                :class="['text-left', 'min-w-[210px] text-darkBlue', { 'pb-2': isButtons }]" 
                :min="sliderRange[0]"
                :max="sliderRange[1]"
                @change="updateSlider" 
              />
              <div class="w-full flex justify-between items-center gap-2">
                <div>
                  <div class="text-sm">{{ $t('from') }}</div>
                  <div class="relative">
                    <input 
                      v-model="minHourlyRate"
                      class="border border-gray-200 rounded-[10px] py-2.5 pr-4 pl-10 max-w-[138px]"
                      @change="(e) => updateSlider([Number((e.target as HTMLInputElement).value), maxHourlyRate])" 
                    />
                    <div class="absolute top-0 left-4 h-full flex items-center">€</div>
                  </div>
                </div>
                <div class="text-center">
                  <div class="h-[1em]"></div>
                  <div class="h-[1px] w-[20px] bg-gray-300 mx-auto"></div>
                </div>
                <div>
                  <div class="text-sm">{{ $t('till') }}</div>
                  <div class="relative">
                    <input 
                      v-model="maxHourlyRate"
                      class="border border-gray-200 rounded-[10px] py-2.5 pr-4 pl-10 max-w-[138px]"
                      @change="(e) => updateSlider([minHourlyRate, Number((e.target as HTMLInputElement).value)])" 
                    />
                    <div class="absolute top-0 left-4 h-full flex items-center">€</div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template #buttons="{ isSelected, closeDropdown }">
            <FilterButtons 
              :isSelected="isSelected"
              class="text-darkBlue"
              @action="closeDropdown"
              @reset="() => {
                closeDropdown()
                resetFilters('hourly_rate')
              }" 
            />
          </template>
        </GTDropdown>
        <div class="h-1 w-[calc(100%+48px)] lg:w-0 lg:h-12 border-t-2 border-l-0 lg:border-t-0 lg:border-l-2 border-darkBlue opacity-5 lg:ml-3 lg:mr-4 " />
        <GTDropdown 
          :title="$t('language')"
          class="w-full lg:w-[18%] text-left"
          labelWrapperClass="justify-between gap-0"
          :isSelected="Boolean(selectedLanguages.length)"
        >
          <template #default="{ isButtons }">
            <GTDropdownOptions 
              v-model="selectedLanguages"
              :class="['text-left', 'min-w-[210px] text-darkBlue', { 'pb-2': isButtons }]"
              :data="allLanguages"
              :multiple="true"
              :checkbox="true"
              class="text-left min-w-[210px]"
              @change="({ label }) => handleSelectFilter('language', label)" 
            />
          </template>
          <template #buttons="{ isSelected, closeDropdown }">
            <FilterButtons 
              :isSelected="isSelected"
              class="text-darkBlue"
              @action="closeDropdown"
              @reset="() => {
                closeDropdown()
                resetFilters('language')
              }" 
            />
          </template>
        </GTDropdown>
        <div class="h-1 w-[calc(100%+48px)] lg:w-0 lg:h-12 border-t-2 border-l-0 lg:border-t-0 lg:border-l-2 border-darkBlue opacity-5 lg:ml-3 lg:mr-4 " />
        <GTDropdown 
          :title="`${$t('name')}/${$t('lastname').toLowerCase()}/${$t('firm').toLowerCase()}`"
          class="w-full lg:w-[18%] text-left text-darkBlue"
          labelClass="*:truncate w-[80%] max-w-[80%] lg:w-[90%] lg:max-w-[90%]"
          labelWrapperClass="justify-between gap-0"
          :isSelected="Boolean(searchQuery)"
          @click="isMobileView && searchModalRef.open()"
        >
          <template #default>
            <ModalSearchLawyers
              ref="searchModalRef"
              v-model="searchQuery"
              :isDesktopNeeded="true"
              :entities="legalEntities"
              :isLoading="isEntitiesLoading"
              @input="onSearchInput"
            />
          </template>
        </GTDropdown>
        <div class="h-1 w-[calc(100%+48px)] lg:w-0 lg:h-12 border-t-2 border-l-0 lg:border-t-0 lg:border-l-2 border-darkBlue opacity-5 lg:ml-3 lg:mr-4 lg:hidden" />
        <button class="py-3 px-6 xl:ml-4 rounded-full h-12 w-full lg:max-w-40 bg-darkBlue hover:bg-lightBlue font-redhat font-semibold select-none" @click="navigateToLawyersPage()">
          {{ $t('filter') }}
        </button>
      </div>
    </div>
  </div>
</template>
